import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { Link } from "react-router-dom";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import { tierForElo } from "@/skate/lib/rank";
import { Crown, MapPin } from "lucide-react";

const TierDot = ({ elo }) => {
  const t = tierForElo(elo);
  return <span className="w-2 h-2 rounded-full inline-block" style={{ background: t.color }} />;
};

export const Leaderboard = () => {
  const { user } = useAuth();
  const [scope, setScope] = useState("global");
  const [city, setCity] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("scope", scope);
      if (scope === "city") params.set("city", city || user?.city || "");
      const { data } = await api.get(`/leaderboard?${params.toString()}`);
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scope, city]);

  useEffect(() => {
    if (scope === "city" && !city && user?.city) setCity(user.city);
  }, [scope, user]);

  return (
    <div data-testid="leaderboard-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">RANKINGS</div>
        <h1 className="font-display text-5xl leading-none">LEADER<br/><span className="text-[#FACC15]">BOARD.</span></h1>
      </div>

      {/* Scope toggle */}
      <div className="grid grid-cols-2 gap-1 bg-zinc-900 p-1 mb-4">
        <button
          onClick={() => setScope("global")}
          data-testid="scope-global"
          className={`h-10 font-display tracking-widest text-sm transition-colors ${scope === "global" ? "bg-[#FACC15] text-white" : "text-zinc-400 hover:text-white"}`}
        >GLOBAL</button>
        <button
          onClick={() => setScope("city")}
          data-testid="scope-city"
          className={`h-10 font-display tracking-widest text-sm transition-colors ${scope === "city" ? "bg-[#FACC15] text-white" : "text-zinc-400 hover:text-white"}`}
        >MY CITY</button>
      </div>

      {scope === "city" && (
        <div className="mb-4 border border-zinc-800 p-3 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-zinc-500" />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            data-testid="city-filter-input"
            className="flex-1 bg-transparent outline-none font-mono text-sm placeholder:text-zinc-600"
          />
        </div>
      )}

      {/* List */}
      <div className="border border-zinc-800 divide-y divide-zinc-900">
        {loading && <div className="p-6 font-mono text-xs text-zinc-500 tracking-widest">LOADING…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center font-mono text-xs text-zinc-500 tracking-widest">NO PLAYERS</div>
        )}
        {rows.map((r, idx) => {
          const tier = tierForElo(r.elo);
          const isMe = r.user_id === user?.user_id;
          return (
            <Link
              to={isMe ? "/skate/profile" : `/skate/skater/${r.user_id}`}
              key={r.user_id}
              data-testid={`leader-row-${r.position}`}
              className={`flex items-center gap-3 p-4 hover:bg-zinc-900 transition-colors ${isMe ? "bg-[#FACC15]/5" : ""}`}
            >
              <div className="w-10 text-right">
                {idx === 0 ? (
                  <Crown className="w-5 h-5 text-yellow-400 inline" />
                ) : (
                  <span className="font-mono text-zinc-500 text-sm">#{r.position}</span>
                )}
              </div>
              <SkaterAvatar user={r} size={40} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display text-lg truncate">@{r.username}</span>
                  {isMe && <span className="font-mono text-[9px] tracking-widest text-[#FACC15] border border-[#FACC15] px-1.5 py-0.5">YOU</span>}
                </div>
                <div className="flex items-center gap-2 font-mono text-[10px] text-zinc-500 tracking-wider">
                  <TierDot elo={r.elo} />
                  <span style={{color: tier.color}}>{tier.name}</span>
                  <span>·</span>
                  <span>{r.city}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="font-mono text-lg font-bold">{r.elo}</div>
                <div className="font-mono text-[9px] text-zinc-500">{r.wins}W / {r.losses}L</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Leaderboard;
